import * as React from 'react';
import * as PXBCore from '@brightlayer-ui/react-components/core';
import { ListItemText, Typography, Divider } from '@mui/material';
import { DataErrorIndicator } from '../DataErrorIndicator';
import { DataState } from '../../types/data-providers';
import { DataEmptyIndicator } from '../DataEmptyIndicator';
import { Icon } from '../../lib/icons';
import { useLanguageLocale, LanguageLocale } from '../../hooks/language-locale-hooks';
import { AlarmBadge } from '../AlarmBadge/AlarmBadge';

export type TimelineEventDetailListItemValue =
    | {
          itemType: 'DeviceName';
          name: string;
          value: string;
      }
    | {
          itemType: 'AlarmStatus';
          trigger: string;
          severity: string;
          channelName: string | null;
          occuredValue: string | number | undefined;
          unit: string;
      }
    | {
          itemType: 'LastDataReceived';
          latestTime: string | null;
          status: string;
      }
    | {
          itemType: 'TimeAlarmSent';
          occuredTime: string;
      }
    | {
          itemType: 'SetPoint';
          threshold: string | number;
          unit: string;
      }
    | {
          itemType: 'ChannelDatum';
          channelName: string | null;
          value: string | number | undefined | null;
          units: string | null;
      }
    | {
          itemType: 'DeviceDetail';
          name: string;
          value: string;
      }
    | {
          itemType: 'NoData';
          icon: Icon;
          message: string;
      }
    | {
          itemType: 'Error';
          message: string;
      };

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getLabel = (languageLocale: LanguageLocale, item: TimelineEventDetailListItemValue) => {
    switch (item.itemType) {
        case 'DeviceName':
            return 'Device Name';
        case 'AlarmStatus':
            return (
                <>
                    <Typography
                        variant="body2"
                        display="inline"
                        style={{
                            lineHeight: 1.25,
                        }}
                    >
                        {languageLocale.t('EVENT_DETAILS.ALARM_STATUS')}
                    </Typography>
                    <Typography
                        variant="body2"
                        display="inline"
                        style={{
                            lineHeight: 1.25,
                            marginLeft: '5px',
                            fontWeight: 600,
                        }}
                    >
                        {item.trigger} {item.channelName}
                    </Typography>
                </>
            );

        case 'LastDataReceived':
            return languageLocale.t('EVENT_DETAILS.LAST_DATA_RECEIVED');

        case 'TimeAlarmSent':
            return languageLocale.t('EVENT_DETAILS.TIME_ALARM_SENT');

        case 'SetPoint':
            return languageLocale.t('EVENT_DETAILS.SET_POINT');

        case 'ChannelDatum':
            return item.channelName || '';

        case 'DeviceDetail':
            return item.name;

        default:
            return undefined;
    }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getValue = (languageLocale: LanguageLocale, item: TimelineEventDetailListItemValue) => {
    switch (item.itemType) {
        case 'DeviceName':
            return item.value;
        case 'AlarmStatus':
            return <PXBCore.ChannelValue value={item.occuredValue || ''} units={item.unit} />;

        case 'LastDataReceived':
            return (
                <>
                    <AlarmBadge alarmStatus={item.status} />
                    <Typography
                        variant="body2"
                        display="inline"
                        style={{
                            lineHeight: 1.25,
                            fontWeight: 600,
                        }}
                    >
                        {languageLocale.formatDateTime(item.latestTime)}
                    </Typography>
                    <Typography
                        variant="body2"
                        display="inline"
                        style={{
                            lineHeight: 1.25,
                            marginLeft: '5px',
                        }}
                    >
                        {languageLocale.timeSince(item.latestTime)}
                    </Typography>
                </>
            );

        case 'TimeAlarmSent':
            return (
                <>
                    <Typography
                        variant="body2"
                        display="inline"
                        style={{
                            lineHeight: 1.25,
                            fontWeight: 600,
                        }}
                    >
                        {languageLocale.formatDateTime(item.occuredTime)}
                    </Typography>
                    <Typography
                        variant="body2"
                        display="inline"
                        style={{
                            lineHeight: 1.25,
                            marginLeft: '5px',
                        }}
                    >
                        {languageLocale.timeSince(item.occuredTime)}
                    </Typography>
                </>
            );

        case 'SetPoint':
            return <PXBCore.ChannelValue value={item.threshold || ''} units={item.unit} />;

        case 'ChannelDatum':
            return <PXBCore.ChannelValue value={item.value || ''} units={item.units || ''} />;

        case 'DeviceDetail':
            return item.value;

        default:
            return undefined;
    }
};

export const TimelineEventDetailListItem: React.FC<{
    value: TimelineEventDetailListItemValue;
    isLast: boolean;
    testId?: string;
}> = (props) => {
    const languageLocale = useLanguageLocale();

    if (props.value.itemType === 'Error') {
        return (
            <div data-testid={props.testId}>
                <div style={{ flex: '1 1 100%', justifyContent: 'center' }}>
                    <DataErrorIndicator
                        data={{ state: DataState.Error, errorMessage: props.value.message }}
                        message={props.value.message}
                        inline
                    />
                </div>
                {!props.isLast && <Divider />}
            </div>
        );
    }

    if (props.value.itemType === 'NoData') {
        return (
            <div data-testid={props.testId}>
                <div style={{ flex: '1 1 100%', justifyContent: 'center' }}>
                    <DataEmptyIndicator
                        icon={props.value.icon}
                        data={{ state: DataState.Loaded, value: {} }}
                        message={props.value.message}
                        inline
                        isEmpty
                    />
                </div>
                {!props.isLast && <Divider />}
            </div>
        );
    }

    const label = getLabel(languageLocale, props.value);
    const value = getValue(languageLocale, props.value);

    return (
        <div data-testid={`${props.testId}-row`}>
            <PXBCore.InfoListItem
                hidePadding
                dense
                title=""
                divider={!props.isLast ? 'full' : undefined}
                leftComponent={
                    typeof label === 'string' ? (
                        <ListItemText>
                            <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                <Typography
                                    variant="body2"
                                    display="inline"
                                    style={{
                                        lineHeight: 1.25,
                                    }}
                                >
                                    {label}
                                </Typography>
                            </div>
                        </ListItemText>
                    ) : (
                        label
                    )
                }
                rightComponent={
                    typeof value === 'string' ? (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {value}
                        </div>
                    ) : (
                        value
                    )
                }
            />
        </div>
    );
};
